import { COOKIE_CATEGORIES } from "@iec-id/shared/constants";
import type { CookieBannerLabels, CookieCategoryLabels } from "@iec-id/ui";
import { ssoOrigin } from "@/lib/sso-url";
import { useLegalT } from "./i18n";

/**
 * Тексты баннера cookie. Кит переводов не знает — строки собираются здесь и
 * приходят в компонент пропом.
 *
 * Набор строится обходом `COOKIE_CATEGORIES`, а не перечислением вручную:
 * добавленная категория без перевода обязана дать видимую дыру (`⟦ключ⟧` в
 * dev), а не молча выпасть из баннера.
 */
export function useCookieBannerLabels(): CookieBannerLabels {
	const t = useLegalT();

	const categories = Object.fromEntries(
		COOKIE_CATEGORIES.map((category) => [
			category.key,
			{
				label: t(`cookies.categories.${category.key}.label` as const),
				hint: t(`cookies.categories.${category.key}.hint` as const),
			} satisfies CookieCategoryLabels,
		]),
	) as CookieBannerLabels["categories"];

	return {
		title: t("cookies.banner.title"),
		description: t("cookies.banner.description"),
		learnMore: t("cookies.banner.learnMore"),
		acceptAll: t("cookies.banner.acceptAll"),
		rejectAll: t("cookies.banner.rejectAll"),
		configure: t("cookies.banner.configure"),
		back: t("cookies.banner.back"),
		save: t("cookies.banner.save"),
		alwaysOn: t("cookies.banner.alwaysOn"),
		close: t("cookies.banner.close"),
		categories,
	};
}

/**
 * Адрес публичного текста документа `cookies`. Ведёт на домен входа: читалка
 * там открывается БЕЗ сессии, а витрина — раздел для владельца аккаунта.
 * Ссылка из баннера обязана работать у гостя (см. отчёт I1).
 *
 * Параметр языка — `lang`: то же имя, что слушает детектор i18next, один
 * параметр на весь фронт (раньше здесь был `locale`).
 */
export function cookiesDocumentHref(locale: string): string {
	return `${ssoOrigin()}/legal/cookies?lang=${locale}`;
}
